import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getCalendarEvents } from '@/api/calendar';
import { useAuth } from '@/contexts/AuthContext';
import type { CalendarEvent } from '@/types/community/calendar';

interface EventSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onEventDataChange?: (event: { id: string; name: string } | null) => void;
}

export const EventSelector: React.FC<EventSelectorProps> = ({
  disabled = false,
  fieldName = 'eventId',
  onEventDataChange
}) => {
  const { t } = useTranslation(['calendar', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch calendar events
  const { data: eventsData, error } = useQuery({
    queryKey: ['calendarEvents', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getCalendarEvents({ 
        firestoreUser,
        pageSize: 100 // Get all events for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize event data from form values when component mounts or form values change
  useEffect(() => {
    const eventId = form.getFieldValue(fieldName);
    if (eventId && eventsData?.events) {
      const event = eventsData.events.find((e: CalendarEvent) => e.id === eventId);
      onEventDataChange?.(event ? { 
        id: event.id || '', 
        name: event.title 
      } : null);
    }
  }, [form, eventsData, onEventDataChange, fieldName]);

  const handleEventChange = (eventId: string) => {
    // Provide event data to parent
    if (eventId) {
      const event = eventsData?.events?.find((e: CalendarEvent) => e.id === eventId);
      onEventDataChange?.(event ? { 
        id: event.id || '', 
        name: event.title 
      } : null);
    } else {
      onEventDataChange?.(null);
    }
  };

  const eventOptions = eventsData?.events?.map((event: CalendarEvent) => ({
    value: event.id,
    label: event.title,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('calendar:fields.title')}
      >
        <Select
          placeholder={t('calendar:fields.selectEvent')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('calendar:fields.title')}
    >
      <Select
        placeholder={t('calendar:fields.selectEvent')}
        onChange={handleEventChange}
        disabled={disabled}
        allowClear
        options={eventOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 